import type { ReactElement, ReactNode } from 'react';

/* The structural pieces every screen repeats: a rhythm container, a section heading, a fact list, and
 * a workflow timeline. Each existed as inline markup on several screens before this, which is how the
 * spacing between two sections came to differ by 4px depending on who wrote the page.
 */

/** Stacks a page's sections at the between-section rhythm. See `.lq-sections` for why it is double. */
export function Sections({ children }: { readonly children: ReactNode }): ReactElement {
  return <div className="lq-sections">{children}</div>;
}

export interface SectionProps {
  readonly title: string;
  readonly hint?: string | undefined;
  /** Marks which part of the product this belongs to, as a leading rule. Used sparingly. */
  readonly accent?: 'quality' | 'governance' | 'ai' | undefined;
  readonly actions?: ReactNode;
  readonly children: ReactNode;
}

export function Section({
  title,
  hint,
  accent,
  actions,
  children,
}: SectionProps): ReactElement {
  return (
    <section className={accent === undefined ? undefined : `lq-section--${accent}`}>
      <div className="lq-section-header">
        <h2 className="lq-section-header__title">{title}</h2>
        {hint !== undefined ? <p className="lq-section-header__hint">{hint}</p> : null}
        {actions !== undefined ? (
          <div style={{ marginLeft: 'auto', display: 'flex', gap: 'var(--space-2)' }}>
            {actions}
          </div>
        ) : null}
      </div>
      {children}
    </section>
  );
}

export interface FactsProps {
  readonly rows: readonly { readonly label: string; readonly value: ReactNode }[];
}

/** Label-above-value pairs. A `<dl>` because that is what it is, which screen readers rely on. */
export function Facts({ rows }: FactsProps): ReactElement {
  return (
    <dl className="lq-facts">
      {rows.map((row) => (
        <div className="lq-facts__row" key={row.label}>
          <dt className="lq-facts__label">{row.label}</dt>
          <dd className="lq-facts__value">{row.value}</dd>
        </div>
      ))}
    </dl>
  );
}

/** Where a thing is in a workflow. `pending` steps stay visible and dimmed — see `.lq-step--pending`. */
export type StepState = 'done' | 'current' | 'pending' | 'failed' | 'skipped';

export interface Step {
  readonly title: string;
  readonly meta?: string | undefined;
  readonly state: StepState;
}

const STEP_COLOUR = {
  done: 'var(--status-ok)',
  current: 'var(--accent)',
  pending: 'var(--border-strong)',
  failed: 'var(--status-error)',
  skipped: 'var(--text-muted)',
} as const satisfies Record<StepState, string>;

export function Steps({ steps }: { readonly steps: readonly Step[] }): ReactElement {
  return (
    <ol className="lq-steps">
      {steps.map((step) => (
        <li
          className={step.state === 'pending' ? 'lq-step lq-step--pending' : 'lq-step'}
          key={step.title}
        >
          <span
            className={
              step.state === 'done' || step.state === 'failed'
                ? 'lq-step__marker lq-step__marker--done'
                : 'lq-step__marker'
            }
            style={{ ['--step-colour' as string]: STEP_COLOUR[step.state] }}
            aria-hidden="true"
          />
          <span className="lq-step__body">
            {/* The state is in the text, not only in the marker's colour: a timeline read aloud has
                to convey which step failed. */}
            <span className="lq-step__title">{step.title}</span>
            {step.meta !== undefined ? (
              <>
                <br />
                <span className="lq-step__meta">{step.meta}</span>
              </>
            ) : null}
          </span>
        </li>
      ))}
    </ol>
  );
}
